import { useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { TaskCard } from './TaskCard'

const SECTION_ORDER = [
  'Claims',
  'Renewals',
  'Amendments',
  'Transfers',
  'Portfolio Reviews',
  'Sales & Onboarding',
  'Client Admin',
  'Quotes & Applications',
  'Will',
  'Medical Aid',
  'Tax',
]

export function TaskList({ tasks, selectedTaskId, onSelectTask, isDarkMode, subtaskCountMap = {} }) {
  const [collapsed, setCollapsed] = useState({})

  // Group tasks by section
  const grouped = {}
  tasks.forEach((t) => {
    const sec = t.section || 'Claims'
    if (!grouped[sec]) grouped[sec] = []
    grouped[sec].push(t)
  })

  // Order sections: known first, then any extras
  const sections = [
    ...SECTION_ORDER.filter((s) => grouped[s]),
    ...Object.keys(grouped).filter((s) => !SECTION_ORDER.includes(s)),
  ]

  const toggleSection = (sec) =>
    setCollapsed((prev) => ({ ...prev, [sec]: !prev[sec] }))

  const headerBg = isDarkMode ? 'bg-slate-800/60' : 'bg-gray-50'
  const headerText = isDarkMode ? 'text-slate-400' : 'text-gray-500'
  const border = isDarkMode ? 'border-slate-800' : 'border-gray-100'

  if (tasks.length === 0) {
    return (
      <div className={`flex items-center justify-center h-48 text-sm ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
        No tasks found. Try adjusting your filters or create a new task.
      </div>
    )
  }

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Column headers */}
      <div className={`flex items-center gap-3 px-4 py-2 text-xs font-medium border-b sticky top-0 z-10 ${headerBg} ${headerText} ${border}`}>
        <div className="w-2 flex-shrink-0" />
        <p className="flex-1 min-w-0">Task / Claim</p>
        <p className="hidden md:block w-36 flex-shrink-0">Client</p>
        <p className="w-24 flex-shrink-0">Due Date</p>
        <p className="hidden sm:block w-20 flex-shrink-0">Status</p>
        <p className="w-6 flex-shrink-0">P</p>
        <p className="w-7 flex-shrink-0" />
        <p className="w-8 flex-shrink-0" />
      </div>

      {sections.map((section) => {
        const sectionTasks = grouped[section]
        const isCollapsed = collapsed[section]

        return (
          <div key={section}>
            {/* Section header */}
            <button
              onClick={() => toggleSection(section)}
              className={`w-full flex items-center gap-2 px-4 py-1.5 text-xs font-semibold border-b transition-colors ${
                isDarkMode
                  ? 'bg-slate-800/40 text-slate-400 border-slate-800 hover:bg-slate-800/60'
                  : 'bg-gray-50/80 text-gray-500 border-gray-100 hover:bg-gray-100'
              }`}
            >
              {isCollapsed
                ? <ChevronRight className="w-3.5 h-3.5 flex-shrink-0" />
                : <ChevronDown className="w-3.5 h-3.5 flex-shrink-0" />
              }
              <span className="uppercase tracking-wide">{section}</span>
              <span className={`ml-1 px-1.5 py-0.5 rounded text-xs font-normal ${
                isDarkMode ? 'bg-slate-700 text-slate-400' : 'bg-gray-200 text-gray-500'
              }`}>
                {sectionTasks.length}
              </span>
            </button>

            {/* Tasks */}
            {!isCollapsed && sectionTasks.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                mode="list"
                onClick={() => onSelectTask(task.id)}
                isSelected={task.id === selectedTaskId}
                isDarkMode={isDarkMode}
                subtaskCount={subtaskCountMap[task.id] || 0}
              />
            ))}
          </div>
        )
      })}
    </div>
  )
}
